import { selectEditorClickedSelection } from 'client/reducers/editor/selectors';
import React, { Suspense, useEffect, useRef } from 'react';
import type { CardData } from 'types/story';
import type { VariableDataType } from 'common/utils/variables/types';
import { getAvailableVariables } from 'common/utils/variables/variables';
import { useClientSelector } from 'client/reducers';
import { selectStory, selectStorySpreadsheetVariables } from 'client/reducers/story/selectors';
import { selectCard } from 'client/reducers/card/selectors';

type Props = {
	html: string;
	onChange: (editorState: any) => void;
	onComplete: () => void;
	editableRef: React.RefObject<HTMLDivElement>;
};

const RichEditor = React.lazy(() => import('admin/components/common/RichEditor'));

const ContentEditable = ({ html, onChange, onComplete, editableRef }: Props) => {
	const selected = useClientSelector(selectEditorClickedSelection);
	const id = editableRef.current?.id ?? '';
	const isSelected = useRef(selected[id] !== undefined);

	const story = useClientSelector(selectStory, () => true)!;
	const spreadsheetVariables = useClientSelector(selectStorySpreadsheetVariables, () => true);
	const card = useClientSelector(selectCard) as CardData;
	const variablesRef = useRef<VariableDataType[]>([]);
	if (variablesRef.current.length === 0) {
		// Get variables once, do not recompute them during next text edit.
		// Variables are used to display [data-mention] and <MentionSuggestions />
		variablesRef.current = getAvailableVariables({ story, spreadsheetVariables, card });
	}

	useEffect(
		function completeOnSelectionChange() {
			if (isSelected.current && !selected[id]) {
				onComplete();
			}

			isSelected.current = selected[id] !== undefined;
		},
		[onComplete, selected, id]
	);

	return (
		// eslint-disable-next-line react/no-danger
		<Suspense fallback={<div dangerouslySetInnerHTML={{ __html: html }} />}>
			<RichEditor
				html={html}
				onChange={onChange}
				mentions={{ variables: variablesRef.current }}
				toolbar={{ colorPicker: { swatches: 'client-story' } }}
			/>
		</Suspense>
	);
};

export default ContentEditable;
